import * as angular from 'angular';
import * as _ from 'lodash';

import {
    ComponentDefinition,
    ConnectionPoint,
    ConnectionPointType,
    ContextualField,
    DateAlignment,
    DateTimeZone,
    GuidObjectType,
    IApplicationConfiguration,
    IExtraIcon,
    IFrequency,
    IFrequencyValidationRule,
    KeyvaultEntry,
    Miniature,
    PropertyType,
    ScriptType,
    TimeInterval,
    TimeUnit
} from '../../../../models';
import * as Fields from '../../../../common/helpers/contextual';
import {
    connectionPointTypes,
    connectionPointRemove,
    guidEmpty,
    Helper,
    importConnectionPointTypes,
    isWideDb,
    outOfSyncConnectionPointTypes,
    cpUsesPassword
} from '../../../../common';
import {
    ICurrentApplicationService,
    ILoggerService,
    IApplicationService,
    IAuthenticationService,
    IIntervalValidationService,
    IStoreService,
    IKeyvaultService,
    IConfigurationService,
    IFileManagementService
} from '../../../../services';
import { connectionPointConfigurationDictionnary, IConnectionPointConfiguration } from './connectionPointConfiguration';
import { Store, StoreElement } from '../../../../stateManagement';
import { Documentation, frequencyErrors } from '../../../../common/helpers/contextual';

class ConnectionPointsContextualController implements angular.IComponentController {
    connectionPointTypes = connectionPointTypes;
    currentConfiguration: IConnectionPointConfiguration;
    connectionPointTypesDictionary = connectionPointConfigurationDictionnary;
    extraIcon: IExtraIcon;
    formController: angular.IFormController;
    frequencyValidationRules: IFrequencyValidationRule[];
    hasKeyVault: boolean = false;
    isLoading: boolean;
    isDatabaseCreationRequested: boolean;
    keyvaultEntries: KeyvaultEntry[];
    loadingDisable: boolean = true;
    loadingMessage: string;
    properties: ContextualField[];
    item: ConnectionPoint & { frequency: IFrequency; isDatabaseCreationRequested: boolean };
    selectedItem: ConnectionPoint & { frequency: IFrequency; isDatabaseCreationRequested: boolean };
    shouldDisableEdit: boolean;
    unselect: () => void;
    testResult: boolean;
    widgetsMiniature: Miniature[];

    private _applicationConfiguration: IApplicationConfiguration;
    private _connectionPoints: ConnectionPoint[];

    private _noBulkInsert: ConnectionPointType[];
    private _defaultDbGuid: string;
    private _libraries: string[];
    private _itemWatchers: (() => void)[];
    private _watchers: (() => void)[];

    /**
     * Dependencies injection
     */
    static $inject = [
        '$scope',
        '$rootScope',
        '$timeout',
        'currentApplicationService',
        'applicationService',
        'authenticationService',
        'loggerService',
        'intervalValidationService',
        'storeService',
        'keyvaultService',
        'fileManagementService'
    ];
    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$timeout: angular.ITimeoutService,
        private _currentApplicationService: ICurrentApplicationService,
        private _applicationService: IApplicationService,
        private _authenticationService: IAuthenticationService,
        private _loggerService: ILoggerService,
        private _intervalValidationService: IIntervalValidationService,
        private _storeService: IStoreService,
        private _keyvaultService: IKeyvaultService,
        private _fileManagementService: IFileManagementService
    ) {}

    /**
     * Initialization
     */
    $onInit() {
        // Get default connection point
        const appConfig = this._currentApplicationService.getApplicationConfiguration();
        this._applicationConfiguration = appConfig;
        const userName = this._authenticationService.authentication.userName;
        this._defaultDbGuid = appConfig.defaultDatabaseGuid;
        this.shouldDisableEdit = true;
        this._watchers = [];
        this._noBulkInsert = [
            ConnectionPointType.ManualFile,
            ConnectionPointType.CSVExport,
            ConnectionPointType.HTTP,
            ConnectionPointType.SMTP,
            ConnectionPointType.WideDatabase,
            ConnectionPointType.WideDatabaseNpgsql
        ];

        this.frequencyValidationRules = [
            {
                key: 'interval',
                isValid: (frequency) => {
                    const isFrequencyValid = this.validateFrequency(frequency);
                    this.selectedItem.interval = frequency.interval;
                    this.selectedItem.intervalTimeUnit = frequency.intervalTimeUnit;
                    return isFrequencyValid;
                }
            }
        ];
        // Check initial connection point type
        this.checkConnectionPointType();

        this._applicationService.getOneWithInformation(userName, appConfig.guid).then((info) => {
            this.loadingDisable = false;
            this.shouldDisableEdit = info.item2.hasActiveJobs;
            this.checkConnectionPointType();
        });

        // Miniature
        this.widgetsMiniature = [
            {
                type: 'connectionPoint',
                title: 'Connection point',
                create: () => {},
                highlights: ['A new connection point'],
                icon: 'fa-database',
                sortingOrder: 0,
                group: ''
            }
        ];

        this._$scope.$watch(
            () => this.hasKeyVault,
            () => {
                if (this.hasKeyVault) {
                    this._keyvaultService.getKeyvaultEntries().then((data) => {
                        this.keyvaultEntries = data;
                    });
                }
            }
        );

        // Connect to store
        this._watchers.push(
            this._storeService.register(
                (store) => this.refreshStore(store),
                [StoreElement.ApiInformation, StoreElement.ConnectionPoints, StoreElement.ConnectionPointsStatuses]
            )
        );
        this._fileManagementService.getAllScriptLibraries(ScriptType.PythonV3).then((data) => {
            this._libraries = data;
            this.refreshProperties();
        });
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['item']) {
            this.onItemChange();
        }
    }

    // Destroy
    $onDestroy() {
        try {
            if (this._watchers && this._watchers.length) {
                this._watchers.forEach((w) => w());
            }
        } catch (e) {
            console.error(e);
        }
    }

    getKeyvaultName(name: string): string {
        const splittedName = name.split('/');
        return splittedName[splittedName.length - 1];
    }

    /**
     * Refresh properties grid when connection point change or connection point type is changed
     * */
    refreshProperties() {
        const customField = angular.copy(Fields.CustomProperties);
        customField.extraData.type = GuidObjectType.ConnectionPoint;
        customField.hide = !this.currentConfiguration.hasUserFields;

        const bulkInsertEnabledField: ContextualField = {
            modelName: 'bulkInsertEnabled',
            propertyType: PropertyType.Boolean,
            label: 'Enable bulk insert',
            propertyName: 'bulkInsertEnabledProperty',
            icon: 'fa-cubes',
            onChange: (value: boolean) => {
                if (this.properties && this.currentConfiguration && this.selectedItem) {
                    const foundProperty = this.properties.find((property) => property.modelName === 'archive');
                    if (foundProperty) {
                        foundProperty.hide = !this.currentConfiguration.hasArchive && !value;
                    }
                }
            },
            hide: this.selectedItem ? this._noBulkInsert.indexOf(this.selectedItem.type) >= 0 : false
        };

        const forecastHorizonField: ContextualField = {
            modelName: 'forecast',
            propertyType: PropertyType.Frequency,
            label: 'Forecast horizon',
            placeholder: 'Forecast horizon',
            propertyName: 'forecastHorizonProperty',
            errorName: 'forecastHorizonPropertyInterval',
            icon: 'fa-fast-forward',
            hide: !(this.selectedItem && isWideDb(this.selectedItem)),
            extraData: {
                frequencyValidationRules: [
                    {
                        key: 'min',
                        isValid: (frequency: IFrequency) => {
                            return frequency.interval === null || frequency.interval === undefined || frequency.interval >= 0;
                        }
                    },
                    {
                        key: 'value',
                        isValid: (freq) => {
                            const value = freq.intervalTimeUnit;
                            const newInterval = new TimeInterval();
                            newInterval.interval = freq.interval;
                            newInterval.unit = value || value === 0 ? value : this.selectedItem.forecast?.intervalTimeUnit;
                            const duration = newInterval.duration();
                            const defaultIntervalInSeconds = new TimeInterval(
                                this.selectedItem.frequency.interval,
                                this.selectedItem.frequency.intervalTimeUnit
                            )
                                .duration()
                                .asSeconds();
                            const isValid = duration.asSeconds() % defaultIntervalInSeconds === 0;
                            return isValid;
                        }
                    }
                ]
            },
            messages: frequencyErrors.concat([{ key: 'value', value: 'Invalid interval. Interval must be a multiple of the database frequency.' }])
        };

        const keyvault = {
            hide: (this.selectedItem && cpUsesPassword.indexOf(this.selectedItem.type) < 0) || !this.hasKeyVault,
            icon: 'fa-key',
            label: 'Keyvault',
            modelName: 'keyvault',
            propertyName: 'keyvaultProperty',
            propertyType: PropertyType.Select,
            required: false,
            extraData: {
                options:
                    this.selectedItem && this.keyvaultEntries
                        ? [{ key: '', value: 'None' }].concat(
                              this.keyvaultEntries.map((d) => {
                                  return { key: this.getKeyvaultName(d.name), value: this.getKeyvaultName(d.name) };
                              })
                          )
                        : [],
                sortAlpha: true,
                sortAscending: true
            }
        };

        const password = {
            hide: !this.currentConfiguration.hasPassword || this.hasKeyVault,
            modelName: 'password',
            propertyType: PropertyType.Password,
            label: 'Password',
            placeholder: 'Password',
            propertyName: 'passwordProperty',
            required: this.currentConfiguration.hasCredentialsMandatory,
            icon: 'fa-lock',
            messages: [{ key: 'required', value: 'A password is required' }]
        };

        const alternateServer = {
            hide: this.selectedItem && importConnectionPointTypes.indexOf(this.selectedItem.type) < 0,
            icon: 'fa-reply fa-rotate-180',
            label: 'Alternate server',
            modelName: 'alternateServerGuid',
            propertyName: 'alternateServerGuidProperty',
            propertyType: PropertyType.Select,
            required: false,
            extraData: {
                options:
                    this.selectedItem && this._connectionPoints
                        ? [{ key: guidEmpty, value: 'None' }].concat(
                              this._connectionPoints
                                  .filter((cp) => cp.type === this.selectedItem.type && cp.guid !== this.selectedItem.guid)
                                  .map((d) => {
                                      return { key: d.guid, value: d.name };
                                  })
                          )
                        : [],
                sortAlpha: true,
                sortAscending: true
            }
        };

        const outOfSyncThreshold: ContextualField = {
            label: 'Out of sync threshold (s)',
            modelName: 'outOfSyncThreshold',
            propertyName: 'outOfSyncThresholdProperty',
            propertyType: PropertyType.Number,
            hide: this.selectedItem ? outOfSyncConnectionPointTypes.indexOf(this.selectedItem.type) < 0 : true,
            icon: 'fa-hourglass',
            required: true,
            messages: [
                {
                    key: 'min',
                    value: 'Threshold must be greater than or equal to 0.'
                },
                {
                    key: 'required',
                    value: 'Threshold must be greater than or equal to 0.'
                }
            ],
            extraData: {
                min: 0
            }
        };

        let availableTypes = angular.copy(this.connectionPointTypes);
        if (this.item?.type !== ConnectionPointType.PI) {
            availableTypes = availableTypes.filter((t) => t.key !== ConnectionPointType.PI);
        }
        if (this.item?.type !== ConnectionPointType.Excel) {
            availableTypes = availableTypes.filter((t) => t.key !== ConnectionPointType.Excel);
        }

        const documentation: ContextualField = Object.assign({}, Documentation);
        documentation.extraData = { placement: 'left' };
        this.properties = [
            {
                modelName: 'name',
                propertyType: PropertyType.Text,
                label: 'Name',
                placeholder: 'Connection point name',
                propertyName: 'connectionPointNameProperty',
                required: true,
                icon: 'fa-id-badge',
                isValid: (value) => this.validateName(value),
                messages: [
                    { key: 'required', value: 'A name is required' },
                    { key: 'duplicate', value: 'This name is already in use' }
                ],
                hide: !this.currentConfiguration.hasName
            },
            customField,
            {
                modelName: 'type',
                propertyType: PropertyType.Select,
                label: 'Type',
                propertyName: 'connectionPointTypeProperty',
                required: true,
                icon: 'fa-connectdevelop',
                extraData: {
                    options: availableTypes,
                    sortAlpha: true,
                    sortAscending: true
                },
                onChange: (value) => {
                    this.selectedItem.type = value;
                    this.checkConnectionPointType();
                    bulkInsertEnabledField.hide = this._noBulkInsert.indexOf(value) >= 0;
                    outOfSyncThreshold.hide = outOfSyncConnectionPointTypes.indexOf(value) < 0;
                    forecastHorizonField.hide = !isWideDb(this.selectedItem);
                    // Set the focus on select type
                    this._$timeout(() => {
                        const element = angular.element('#connectionPointTypeProperty');
                        if (element.length > 0) {
                            (element[0] as HTMLElement).focus();
                        }
                        if (this.formController) {
                            this.formController.$setDirty();
                        }
                    });
                },
                hide: !this.currentConfiguration.hasConnectionPointType
            },
            alternateServer,
            {
                modelName: 'address',
                propertyType: PropertyType.Text,
                label: 'Address',
                placeholder: 'Address',
                propertyName: 'addressProperty',
                required: this.currentConfiguration.hasAddressMandatory,
                icon: 'fa-map-marker',
                messages: [{ key: 'required', value: 'An address is required' }],
                hide: !this.currentConfiguration.hasAddress
            },
            {
                modelName: 'schema',
                propertyType: PropertyType.Text,
                label: 'Schema',
                placeholder: 'Schema',
                propertyName: 'schemaProperty',
                icon: 'fa-window-restore',
                messages: [],
                hide: !this.currentConfiguration.hasSchema
            },
            {
                modelName: 'instance',
                propertyType: PropertyType.Text,
                label: 'Database',
                placeholder: 'Database',
                propertyName: 'databaseProperty',
                required: this.currentConfiguration.hasDatabaseMandatory,
                icon: 'fa-database',
                messages: [{ key: 'required', value: 'A database is required' }],
                hide: !this.currentConfiguration.hasDatabase
            },
            {
                modelName: 'userName',
                propertyType: PropertyType.UserName,
                label: 'User Name',
                placeholder: 'User name',
                propertyName: 'userNameProperty',
                required: this.currentConfiguration.hasCredentialsMandatory,
                icon: 'fa-user',
                messages: [{ key: 'required', value: 'A user name is required' }],
                hide: !this.currentConfiguration.hasUsername
            },
            keyvault,
            password,
            {
                modelName: 'isAutoLoad',
                propertyType: PropertyType.Boolean,
                label: 'Auto load',
                propertyName: 'isAutoLoadProperty',
                iconText: '⥁ ',
                hide: !this.currentConfiguration.hasAutoLoad
            },
            bulkInsertEnabledField,
            {
                modelName: 'dateAlignment',
                propertyType: PropertyType.Select,
                label: 'Date alignment',
                placeholder: 'Date alignment',
                propertyName: 'dateAlignmentProperty',
                required: true,
                icon: 'fa-map-signs',
                hide: this.selectedItem?.type !== ConnectionPointType.DataFolder,
                extraData: {
                    options: [
                        { key: DateAlignment.IgnoreNonMatching, value: 'Ignore nonmatching' },
                        { key: DateAlignment.AlignOnPrevious, value: 'Align nonmatching on previous date' }
                    ]
                }
            },
            {
                modelName: 'archive',
                propertyType: PropertyType.Text,
                label: 'Path to archive folder (leave empty to disable archiving)',
                placeholder: 'Archive',
                propertyName: 'archiveProperty',
                required: this.currentConfiguration.hasArchiveMandatory,
                icon: 'fa-file-archive-o',
                messages: [
                    {
                        key: 'required',
                        value: 'An archive path is required'
                    }
                ],
                hide: !this.currentConfiguration.hasArchive && !this.selectedItem?.bulkInsertEnabled
            },
            {
                modelName: 'scriptFile',
                propertyType: PropertyType.Select,
                label: 'Leave this field empty to trigger data acquisition or reference a V3 Python script to execute custom data processing instead',
                placeholder: 'Script file',
                propertyName: 'scriptFileProperty',
                iconText: '.py',
                hide: !this.currentConfiguration.hasScriptFile,
                extraData: {
                    options: [{ key: '', value: '' }].concat((this._libraries || []).map((l) => ({ key: l.toLocaleLowerCase(), value: l })))
                }
            },
            {
                modelName: 'timeZone',
                propertyType: PropertyType.Select,
                label: 'Time zone',
                propertyName: 'dateTimeZoneProperty',
                disabled: false,
                required: this.currentConfiguration.hasTimeZone,
                hide: !this.currentConfiguration.hasTimeZone,
                icon: 'fa-clock-o',
                extraData: {
                    options: [
                        { key: DateTimeZone.ApplicationTimezone, value: 'Application time zone' },
                        { key: DateTimeZone.Utc, value: 'UTC' }
                    ],
                    sortAlpha: true,
                    sortAscending: true
                }
            },
            {
                modelName: 'tagDefinitionFile',
                propertyType: PropertyType.Text,
                label: 'Tags File',
                placeholder: 'Tags file',
                propertyName: 'tagsFileProperty',
                icon: 'fa-file-excel-o',
                hide: !this.currentConfiguration.hasTagsFile,
                messages: [{ key: 'required', value: 'blabla required' }]
            },
            {
                modelName: 'isExportingLogbook',
                propertyType: PropertyType.Boolean,
                label: 'Export logbook entries',
                propertyName: 'exportLogbookProperty',
                icon: 'fa-book',
                hide: !this.currentConfiguration.hasExportLogbook
            },
            {
                modelName: 'ignoreSSLError',
                propertyType: PropertyType.Boolean,
                label: 'Ignore SSL errors',
                propertyName: 'ignoreSSLErrorsProperty',
                icon: 'fa-expeditedssl',
                hide: !this.currentConfiguration.hasIgnoreSSLErrors
            },
            {
                modelName: 'allowOverwrite',
                propertyType: PropertyType.Boolean,
                label: 'Allow Overwrite',
                propertyName: 'allowOverwriteProperty',
                icon: 'fa-refresh',
                hide: !this.currentConfiguration.hasAllowOverwrite
            },
            {
                modelName: 'frequency',
                propertyType: PropertyType.Frequency,
                label: 'Interval',
                placeholder: 'Interval',
                propertyName: 'frequencyProperty',
                errorName: 'frequencyPropertyInterval',
                icon: 'fa-clock-o',
                required: !this.currentConfiguration.hasFrequency,
                extraData: {
                    intervalUnitName: 'intervalTimeUnit',
                    frequencyValidationRules: this.frequencyValidationRules
                },
                messages: [{ key: 'interval', value: 'Unsupported time interval. Refer to section 3.1 of WiDE documentation for supported intervals.' }],
                hide: !this.currentConfiguration.hasFrequency
            },
            {
                modelName: 'isDatabaseCreationRequested',
                propertyType: PropertyType.Boolean,
                label: 'Create database',
                propertyName: 'createDatabaseProperty',
                icon: 'fa-plus-square',
                hide: !this.currentConfiguration.hasCreateDatabase
            },
            {
                modelName: 'lagTime',
                propertyType: PropertyType.Number,
                label: 'Lag time (s)',
                propertyName: 'lagTimeProperty',
                icon: 'fa-hourglass-start',
                hide: !this.currentConfiguration.hasAutoLoad || !this.selectedItem.isAutoLoad,
                isValid: (value) => this.validateLagTime(value),
                messages: [{ key: 'float', value: 'Lag time must be a positive number' }]
            },
            forecastHorizonField,
            outOfSyncThreshold,
            {
                modelName: 'isSsl',
                propertyType: PropertyType.Boolean,
                label: 'Use STARTTLS',
                propertyName: 'useSSLProperty',
                icon: 'fa-expeditedssl',
                hide: !this.currentConfiguration.hasUseSSL
            },
            {
                modelName: 'defaultSender',
                propertyType: PropertyType.Text,
                label: 'Default Sender',
                placeholder: 'Default sender',
                propertyName: 'defaultSenderProperty',
                required: this.currentConfiguration.hasMandatoryDefaultSender,
                icon: 'fa-envelope-o',
                messages: [
                    {
                        key: 'required',
                        value: 'A default sender is required'
                    }
                ],
                hide: !this.currentConfiguration.hasDefaultSender
            },
            {
                modelName: 'querySetGuid',
                propertyType: PropertyType.ConnectionPointQuerySet,
                propertyName: 'querySetGuidProperty',
                label: 'Query set',
                placeholder: 'Query set',
                icon: 'fa-file-code-o',
                required: this.currentConfiguration.hasQuerySet,
                hide: !this.currentConfiguration.hasQuerySet,
                extraData: {
                    type: this.selectedItem ? this.selectedItem.type : undefined
                },
                messages: [{ key: 'required', value: 'A query set is required' }],
                isValid: (value) => this.validateQuerySet(value)
            },
            {
                modelName: 'ignoreErrors',
                propertyType: PropertyType.Boolean,
                label: 'Ignore errors',
                propertyName: 'ignoreErrorsProperty',
                icon: 'fa-eye-slash'
            },
            documentation
        ];
        if (this._applicationConfiguration?.isChildApplicationCreationAllowed) {
            this.properties.push(angular.copy(Fields.IsExportToChildApplication));
        }
    }

    /**
     * Set the frequency in ConnectionPoint
     */
    setFrequency(cp: { interval: number; intervalTimeUnit: TimeUnit; frequency?: IFrequency }) {
        if (cp) {
            cp.frequency = {
                interval: cp.interval,
                intervalTimeUnit: cp.intervalTimeUnit
            };
        }
    }

    /**
     * Check if connection point type has changed and refresh fields and properties accordingly
     */
    checkConnectionPointType() {
        if (this.selectedItem && this.selectedItem.type >= 0) {
            this.currentConfiguration = this.connectionPointTypesDictionary[this.selectedItem.type];
        } else {
            this.currentConfiguration = this.connectionPointTypesDictionary[ConnectionPointType.Manual];
        }

        this.resetFieldOnChangeType();
        this.refreshProperties();
        this.mayDisableWideDb();
    }

    private mayDisableWideDb() {
        if (this.selectedItem && this.selectedItem.guid === this._defaultDbGuid && this.shouldDisableEdit) {
            this.extraIcon = {
                title: 'Please stop all active jobs if you want to modify your default WiDE database',
                icon: this.loadingDisable ? 'fa fa-fw fa-spinner fa-pulse' : 'fa-exclamation-triangle inline-warning'
            };
            const props = angular.copy(this.properties);
            props.forEach((p) => (p.disabled = true));
            this.properties = props;
        } else {
            this.extraIcon = undefined;
        }
    }

    /**
     * Reset fields values
     */
    resetFieldOnChangeType() {
        const cp = this.selectedItem;
        const cpc = this.currentConfiguration;
        if (cp) {
            if (!cpc.hasAddress) {
                cp.address = null;
            }
            if (!cpc.hasUsername) {
                cp.userName = null;
            }
            if (!cpc.hasPassword) {
                cp.password = null;
            }
            if (!cpc.hasFrequency) {
                cp.interval = 0;
            } else if (cp.interval === 0) {
                cp.interval = 1;
            }
            if (!cpc.hasDatabase) {
                cp.instance = null;
            }
            if (!cpc.hasTagsFile) {
                cp.tagDefinitionFile = null;
            }
            if (!cpc.hasDefaultSender) {
                cp.defaultSender = null;
            }
            this.setFrequency(cp);
        }
    }

    /**
     * Validate connection point name
     */
    validateName(value: string): { [key: string]: boolean } {
        const hasDuplicate =
            this._connectionPoints && _.some(this._connectionPoints, (cp) => cp.name && cp.name === value && this.selectedItem.guid !== cp.guid);

        return Helper.getDictionnary<boolean>([
            { key: 'required', value: !!value },
            { key: 'duplicate', value: !hasDuplicate }
        ]);
    }

    validateQuerySet(value): { [key: string]: boolean } {
        return Helper.getDictionnary<boolean>([{ key: 'required', value: this.selectedItem.querySetGuid !== guidEmpty }]);
    }

    validateLagTime(value?: number): { [key: string]: boolean } {
        return Helper.getDictionnary<boolean>([{ key: 'float', value: !value || value >= 0 }]);
    }

    /**
     * Validate connection point frequency
     * @param frequency Frequency to validate (must match application frequency, except for data generation)
     */
    validateFrequency(frequency): boolean {
        if (this.selectedItem.type === ConnectionPointType.DataGeneration) {
            if (frequency.interval > 0) {
                return true;
            } else {
                return false;
            }
        } else {
            const isDbWide = isWideDb(this.selectedItem);
            return this._intervalValidationService.isValidComplete(frequency, isDbWide, false, isDbWide);
        }
    }

    /**
     * Save connection point properties
     */
    save() {
        this.isLoading = true;
        if (this.selectedItem.frequency) {
            this.selectedItem.interval = this.selectedItem.frequency.interval;
            this.selectedItem.intervalTimeUnit = this.selectedItem.frequency.intervalTimeUnit;
        }
        if (this.selectedItem.isDatabaseCreationRequested) {
            this.isDatabaseCreationRequested = this.selectedItem.isDatabaseCreationRequested;
            delete this.selectedItem.isDatabaseCreationRequested;
        }
        if (this.selectedItem.forecast && (this.selectedItem.forecast.interval === undefined || this.selectedItem.forecast.interval === null)) {
            // reset forecast if not 0
            this.selectedItem.forecast = undefined;
        }

        return this._storeService
            .dispatchers()
            .connectionPoints.updateOne(this.selectedItem, this.isDatabaseCreationRequested)
            .then((saved) => {
                if (saved.guid === this._defaultDbGuid) {
                    this._currentApplicationService
                        .refreshCurrentApplication()
                        .catch(() => this._loggerService.logError('Failed to refresh connection point.'));
                }
                this.selectedItem.creating = false;
                this._loggerService.logSuccess('Connection point successfully updated.');
                this.formController.$setPristine();
                this.isDatabaseCreationRequested = false;
                return false;
            })
            .catch((reason) => {
                const msg = 'Failed to update connection point' + (reason && reason.data && reason.data.message ? ` : ${reason.data.message}` : '.');
                this._loggerService.logError(msg);
                return true;
            })
            .finally(() => (this.isLoading = false));
    }

    /**
     * Cancel current connection point properties changes
     */
    cancel(): void {
        if (this.selectedItem.creating) {
            this._$rootScope.$broadcast(connectionPointRemove, this.selectedItem.guid);
        }
        this.unselect();
    }

    private refreshStore(store: Store) {
        this._connectionPoints = _.values(store.connectionPoints.dictionary);
        this.hasKeyVault = store.apiInformation.value.hasKeyVault;
    }

    private showHideLagTime(): void {
        const lagTimeProperty = _.find(this.properties, (p) => p.modelName === 'lagTime');
        lagTimeProperty.hide = !this.currentConfiguration.hasAutoLoad || !this.selectedItem.isAutoLoad;
    }

    // release current watchers
    private releaseItemWatchers() {
        if (this._itemWatchers) {
            this._itemWatchers.forEach((w) => w());
        }
        this._itemWatchers = [];
    }

    // Triggered when selectedItem change
    private onItemChange() {
        // reinit
        this.releaseItemWatchers();
        this.selectedItem = angular.copy(this.item);

        // initialize tz, freq and type
        if (this.selectedItem && this.selectedItem.timeZone === undefined) {
            this.selectedItem.timeZone = DateTimeZone.Utc;
        }
        this.setFrequency(this.selectedItem);
        this.checkConnectionPointType();

        if (this.selectedItem) {
            // watcher when selecting isAutoLoad
            this._itemWatchers.push(
                this._$scope.$watch(
                    () => this.selectedItem?.isAutoLoad ?? false,
                    (newValue, oldValue) => {
                        if (newValue !== oldValue) {
                            this.showHideLagTime();
                        }
                    }
                )
            );
        }
    }
}

export const wtConnectionPointsContextual: ComponentDefinition = {
    componentId: 'wtConnectionPointsContextual',
    controller: ConnectionPointsContextualController,
    template: require('./connectionPointsContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        item: '<',
        formController: '=',
        unselect: '&'
    }
};
